import React, { useEffect, useState } from 'react';
import { Keyboard, X, ShieldAlert, Cpu, Sparkles, RefreshCw, KeyRound, Layers } from 'lucide-react';
import { DEFAULT_HOTKEYS, NexoraXHotkey, formatHotkey } from './NexoraXHotkeysManager';

interface NexoraXHotkeyHelpModalProps {
  onClose: () => void;
}

export const NexoraXHotkeyHelpModal: React.FC<NexoraXHotkeyHelpModalProps> = ({ onClose }) => {
  const [activeHotkeys, setActiveHotkeys] = useState<NexoraXHotkey[]>([]);
  const [recordingId, setRecordingId] = useState<string | null>(null);

  const loadKeys = () => {
    try {
      const cached = localStorage.getItem('nexorax_NexoraX_hotkeys');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          // Mix with default metadata descriptions for visualization
          const mapped = parsed.map((h: any) => {
            const base = DEFAULT_HOTKEYS.find(df => df.id === h.id || df.action === h.action);
            return {
              id: h.id || base?.id || '',
              name: h.name || base?.name || h.action,
              action: h.action,
              key: h.key,
              ctrlKey: !!h.ctrlKey,
              altKey: !!h.altKey,
              shiftKey: !!h.shiftKey,
              metaKey: !!h.metaKey,
              description: base?.description || 'No direct metadata payload provided for this trigger.'
            } as NexoraXHotkey;
          });
          setActiveHotkeys(mapped);
          return;
        }
      }
    } catch (err) {
      console.error("Error loading help modal hotkeys:", err);
    }
    setActiveHotkeys(DEFAULT_HOTKEYS);
  };

  useEffect(() => {
    // Lock background scroll when open
    document.body.style.overflow = 'hidden';
    
    loadKeys();
    window.addEventListener('nexorax-hotkeys-reloaded', loadKeys);
    
    // Close on escape key press
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !recordingId) onClose();
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('nexorax-hotkeys-reloaded', loadKeys);
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose, recordingId]);

  // Sniffer keyboard listener during active override state
  useEffect(() => {
    if (!recordingId) return;

    function handleKeyDown(e: KeyboardEvent) {
      e.preventDefault();
      e.stopPropagation();

      const key = e.key;
      if (['Control', 'Alt', 'Shift', 'Meta'].includes(key)) return;

      const updated = activeHotkeys.map(hk => {
        if (hk.id === recordingId) {
          return {
            ...hk,
            key: key,
            ctrlKey: e.ctrlKey,
            altKey: e.altKey,
            shiftKey: e.shiftKey,
            metaKey: e.metaKey
          };
        }
        return hk;
      });

      setActiveHotkeys(updated);
      setRecordingId(null);

      // Save custom override configuration to localStorage
      localStorage.setItem('nexorax_NexoraX_hotkeys', JSON.stringify(updated));

      // Dispatch global calibration reload event
      window.dispatchEvent(new CustomEvent('nexorax-hotkeys-reloaded', { detail: updated }));
    }

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [recordingId, activeHotkeys]);

  const handleResetToDefaults = () => {
    if (window.confirm("Restore custom hotkey allocations to factory defaults?")) {
      const reset = JSON.parse(JSON.stringify(DEFAULT_HOTKEYS));
      setActiveHotkeys(reset);
      localStorage.setItem('nexorax_NexoraX_hotkeys', JSON.stringify(reset));
      window.dispatchEvent(new CustomEvent('nexorax-hotkeys-reloaded', { detail: reset }));
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-fade-in font-mono">
      <div 
        className="relative w-full max-w-4xl bg-[#030712] border-2 border-cyan-500/30 rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(6,182,212,0.15)] flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        id="NexoraX-hotkey-help-modal"
      >
        {/* Top Decorative Scanning Line */}
        <div className="h-[2px] w-full bg-gradient-to-r from-cyan-500 via-fuchsia-500 to-emerald-500"></div>

        {/* Modal Header */}
        <div className="flex justify-between items-center bg-[#070d1e] border-b border-slate-900 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-950/40 border border-cyan-800/40 rounded-lg text-[#00ffff]">
              <Keyboard className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <div className="text-white font-sans font-black text-xs uppercase tracking-[0.25em] leading-none mb-1">
                NexoraX Keyboard Matrix Directory //
              </div>
              <div className="text-[10px] text-cyan-400 font-extrabold uppercase tracking-[0.15em] flex items-center gap-1.5 leading-none">
                <span className="inline-block w-1.5 h-1.5 bg-[#00ffff] rounded-full animate-ping"></span>
                ACTIVE MONITORING & INGRESS CONTROL
              </div>
            </div>
          </div>

          <button 
            onClick={onClose}
            className="p-1.5 text-slate-500 hover:text-white bg-slate-950/50 border border-slate-900 rounded-lg hover:border-slate-800 transition-all cursor-pointer"
            id="close-hotkey-help-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-[#111827]/40 border-b border-slate-900/60 px-6 py-3.5 flex items-start gap-3">
          <ShieldAlert className="w-4 h-4 text-fuchsia-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-slate-400 leading-relaxed uppercase">
            Click the <span className="text-cyan-400 font-extrabold">Re-map</span> key button on any card to overwrite global shortcut triggers, or restore defaults instantly. Direct live calibration saves overrides to local persistence storage.
          </p>
        </div>

        {/* Calibration Sniffer Banner */}
        {recordingId && (
          <div className="mx-6 mt-4 p-4 rounded-xl bg-fuchsia-950/20 border border-fuchsia-500/40 text-center animate-pulse flex flex-col items-center justify-center gap-1 font-mono">
            <KeyRound className="w-6 h-6 text-fuchsia-450 animate-bounce" />
            <h4 className="text-xs font-black text-fuchsia-350 uppercase">SHORTCUT CALIBRATION MATRIX ACTIVE</h4>
            <p className="text-[10px] text-slate-450 max-w-xl">
              Ready in cockpit. Press any key combination (e.g. <kbd className="bg-slate-950 text-white px-1.5 py-0.5 rounded border border-slate-800 text-[9px]">Alt + S</kbd> or <kbd className="bg-slate-950 text-white px-1.5 py-0.5 rounded border border-slate-800 text-[9px]">Shift + G</kbd>) to override shortcut bindings.
            </p>
          </div>
        )}

        {/* Scrollable Hotkeys Table */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeHotkeys.map((hk) => {
              const formattedCombo = formatHotkey(hk);
              const isGlobalToggle = hk.id === 'toggle-marketplace' || hk.id === 'toggle-admin';
              const isRecordingThis = recordingId === hk.id;
              
              return (
                <div 
                  key={hk.id || hk.action}
                  className={`p-4 rounded-xl border transition-all duration-300 flex flex-col justify-between ${
                    isGlobalToggle 
                      ? 'bg-cyan-950/10 border-cyan-500/20 hover:border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.05)]' 
                      : 'bg-black/40 border-slate-900 hover:border-slate-800/80'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="font-mono text-[11px] font-black text-purple-300 uppercase tracking-wider">
                        {hk.name}
                      </span>
                      {isGlobalToggle && (
                        <span className="text-[8px] font-sans font-extrabold text-cyan-400 bg-cyan-900/30 px-1.5 py-0.5 border border-cyan-800/50 rounded uppercase tracking-widest leading-none shrink-0">
                          GLOBAL ROUTE
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 font-sans uppercase leading-relaxed mb-4 text-left">
                      {hk.description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between mt-auto pt-3 border-t border-slate-900/60 gap-4">
                    <button
                      onClick={() => setRecordingId(hk.id)}
                      className={`text-[9.5px] px-2 py-1.5 border transition-all rounded font-bold uppercase flex items-center gap-1 cursor-pointer bg-slate-950 hover:bg-slate-900 ${
                        isRecordingThis
                          ? 'border-fuchsia-500 text-fuchsia-400 animate-pulse'
                          : 'border-slate-800 hover:border-cyan-500/40 text-slate-400 hover:text-cyan-400'
                      }`}
                    >
                      <KeyRound className="w-3 h-3" />
                      {isRecordingThis ? 'Sniffing...' : 'Re-map'}
                    </button>

                    <span className={`text-[10px] font-black px-2.5 py-1.5 rounded shadow-inner leading-none ${
                      isRecordingThis
                        ? 'text-fuchsia-400 bg-fuchsia-950/30 animate-pulse border border-fuchsia-500/50'
                        : isGlobalToggle
                        ? 'text-white bg-cyan-950/80 border border-cyan-500/50'
                        : 'text-emerald-400 bg-slate-950 border border-slate-800'
                    }`}>
                      {isRecordingThis ? 'PRESS KEYS' : formattedCombo}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-[#050914] border-t border-slate-900 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="text-[9px] text-slate-600 uppercase flex items-center gap-2">
            <Cpu className="w-3.5 h-3.5 text-slate-600" />
            <span>NEXORAX COGNITIVE MATRIX // HARDWARE SHIELD ENCRYPTED</span>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleResetToDefaults}
              className="bg-slate-950 hover:bg-slate-900 border border-slate-850 hover:border-slate-700 text-slate-400 hover:text-white px-4 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-wider transition-all cursor-pointer"
            >
              Reset to Defaults
            </button>
            <span className="text-[9px] text-[#10b981] font-extrabold uppercase bg-emerald-950/20 border border-emerald-800/30 px-2 py-0.5 rounded leading-none hidden sm:inline-block">
              REPLICA MATRIX SECURE
            </span>
            <button 
              onClick={onClose}
              className="bg-gradient-to-r from-red-950/30 to-rose-900/30 border border-rose-500/50 hover:from-rose-950/40 hover:to-rose-900/40 text-rose-300 px-5 py-2 rounded-lg font-mono font-bold text-[10px] uppercase tracking-wider transition-all cursor-pointer"
              id="dismiss-help-btn"
            >
              [ VERIFY & DISMISS ]
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
